//! Programmatic entry point for the Drive file picker: the dialog that
//! replaces the OS file dialog everywhere the app asks the user for a file
//! (Import, Attach to chat, Insert file). Callers await `pickFilesFromDrive`
//! and receive plain `File`s, whether the user picked an existing Drive file
//! or uploaded one from their computer, so downstream import/attach pipelines
//! are unaffected by where the file came from.
//!
//! A `DriveFilePickerHost` mounted by each app surface (sheet, dashboard)
//! registers the actual dialog opener. When no host is mounted (e.g. a public
//! file viewed without team access), the OS picker is used as a fallback so
//! callers never have to care.

import { uploadFile } from '@/app/helpers/files';
import { apiClient } from '@/shared/api/apiClient';
import type { Document } from 'quadratic-shared/typesAndSchemasDocuments';

export type DriveFilePickerOptions = {
  /** Dialog title, e.g. “Attach a file”. */
  title: string;
  /**
   * Accepted file types, in `<input accept>` syntax: extensions (`.csv`) and
   * MIME types with optional wildcard subtype (`image/*`). Empty/undefined
   * accepts everything.
   */
  accept?: string[];
  multiple?: boolean;
  /** Where the picker was opened from; used for analytics only. */
  source: string;
};

type DriveFilePickerOpener = (options: DriveFilePickerOptions) => Promise<File[]>;

type DriveFilePickerRegistration = {
  open: DriveFilePickerOpener;
  cancel: () => void;
};

// Hosts can nest: Agent Chat's in-page file preview mounts a sheet UI — and
// with it a second host — on top of the dashboard's. A stack keeps the
// innermost host active and restores the outer one when the inner unmounts,
// where a single slot would be nulled by the inner host's cleanup and leave
// the still-mounted outer host unregistered.
const registrations: DriveFilePickerRegistration[] = [];

const activeRegistration = (): DriveFilePickerRegistration | undefined => registrations[registrations.length - 1];

/** Called by `DriveFilePickerHost` on mount/unmount. */
export const registerDriveFilePicker = (open: DriveFilePickerOpener, cancel: () => void): (() => void) => {
  const registration: DriveFilePickerRegistration = { open, cancel };
  registrations.push(registration);
  return () => {
    const index = registrations.indexOf(registration);
    if (index !== -1) registrations.splice(index, 1);
  };
};

/**
 * Closes the active host's open picker and answers its caller with a cancel.
 * Used when the context a pick was made in goes away — switching files leaves
 * the host mounted, but the sheet, cursor and team the caller captured no
 * longer exist.
 */
export const cancelDriveFilePicker = (): void => {
  activeRegistration()?.cancel();
};

/**
 * Opens the Drive file picker and resolves with the chosen files (empty on
 * cancel). Falls back to the OS file dialog when no picker host is mounted.
 */
export const pickFilesFromDrive = (options: DriveFilePickerOptions): Promise<File[]> => {
  const active = activeRegistration();
  if (active) return active.open(options);
  return uploadFile(options.accept ?? []);
};

// Dropped files frequently arrive with an empty `File.type` (the OS has no
// MIME registered for the extension, or the drag source supplied none), which
// would make a wildcard filter like `image/*` reject a .png that Browse
// accepts. Covers the types the app's accept lists actually name.
const MIME_TYPE_BY_EXTENSION: Record<string, string> = {
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.svg': 'image/svg+xml',
  '.bmp': 'image/bmp',
  '.avif': 'image/avif',
  '.heic': 'image/heic',
  '.tif': 'image/tiff',
  '.tiff': 'image/tiff',
  '.ico': 'image/x-icon',
  '.pdf': 'application/pdf',
};

const mimeTypeFromName = (lowerName: string): string => {
  const dot = lowerName.lastIndexOf('.');
  if (dot === -1) return '';
  return MIME_TYPE_BY_EXTENSION[lowerName.slice(dot)] ?? '';
};

/**
 * Whether a file (Drive document or local upload) satisfies an `<input
 * accept>`-style filter list. Extensions match the file name; MIME entries
 * match the type, with `type/*` wildcards supported, falling back to the
 * extension when the type is unknown. An empty list accepts everything.
 */
export const matchesAccept = (mimeType: string, name: string, accept?: string[]): boolean => {
  if (!accept || accept.length === 0) return true;
  const lowerName = name.toLowerCase();
  const mime = mimeType.split(';')[0].trim().toLowerCase() || mimeTypeFromName(lowerName);
  return accept.some((entry) => {
    const filter = entry.trim().toLowerCase();
    if (filter.startsWith('.')) return lowerName.endsWith(filter);
    if (filter.endsWith('/*')) return mime.startsWith(filter.slice(0, -1));
    return mime === filter;
  });
};

/**
 * Whether a local `File` satisfies an `<input accept>`-style filter list. The
 * OS picker enforces `accept` itself; drag-and-drop does not, so dropped files
 * have to be filtered by hand to match what Browse and the Drive tab allow.
 */
export const fileMatchesAccept = (file: File, accept?: string[]): boolean =>
  matchesAccept(file.type, file.name, accept);

// Which Drive document each picked file came from. Callers receive plain
// `File`s so import/attach pipelines don't have to care where a file came
// from, but the ones that would otherwise store the file in Drive (chat
// attach, import with “Keep originals in Drive”) need to know it is already
// there, or they create a second copy of it.
const driveDocumentUuidByFile = new WeakMap<File, string>();

/**
 * The Drive document a picked file came from, or undefined when the user
 * uploaded it from their computer.
 */
export const driveDocumentUuid = (file: File): string | undefined => driveDocumentUuidByFile.get(file);

/** Downloads a Drive document and wraps it as a `File` for import/attach pipelines. */
export const documentToFile = async (document: Document): Promise<File> => {
  const response = await fetch(document.downloadUrl);
  if (!response.ok) {
    throw new Error(`Failed to download document ${document.uuid}: ${response.status}`);
  }
  const blob = await response.blob();
  const file = new File([blob], document.name, { type: document.mimeType });
  driveDocumentUuidByFile.set(file, document.uuid);
  return file;
};

/** Fetches a Drive document by uuid and converts it to a `File`. */
export const documentUuidToFile = async (documentUuid: string): Promise<File> => {
  const { document } = await apiClient.documents.get(documentUuid);
  return documentToFile(document);
};
